import { Button } from '@bpinternal/ui-kit-next'
import { FC } from 'react'
import { showPlanSelectionDialog } from '../components/PlanSelectionDialog'
import { BillingVersion, Plan } from '../types/index'
import cyberSaleBackgroundImg from '~/assets/saleEvents/cyber-sale-2025/cyber-sale.png'
import cyberSaleButtonBgImg from '~/assets/saleEvents/cyber-sale-2025/cyber-sale-button.png'
import { Link } from '@tanstack/react-router'
import { Gift } from 'lucide-react'

type SaleDiscount = {
  promoCode?: string
  couponId?: string
}

type SaleEvent = {
  id: string
  displayName: string
  startDate: Date
  endDate: Date
  isElligible: ({
    nextPhasePlan,
    cancellationDate,
    billingVersion,
  }: {
    nextPhasePlan: Plan
    cancelWindowStart: Date
    cancelWindowEnd: Date
    cancellationDate?: Date
    billingVersion?: BillingVersion
  }) => boolean
  planDiscounts: Partial<
    Record<
      Plan,
      {
        percentOff: number
        monthly?: SaleDiscount
        yearly?: SaleDiscount
      }
    >
  >
  Button: FC<{ workspaceId: string }>
  Banner: FC<{ workspaceId: string }>
}

export const SALE_EVENTS: SaleEvent[] = [
  {
    id: 'cyber-sale-2025-november',
    displayName: 'Cyber Sale',
    startDate: new Date('2025-11-24T00:00:00'),
    endDate: new Date('2025-11-30T23:59:59'),
    isElligible: ({ nextPhasePlan, cancellationDate, billingVersion, cancelWindowStart, cancelWindowEnd }) => {
      const cancelledDuringSale = cancellationDate
        ? new Date(cancellationDate) >= cancelWindowStart && new Date(cancellationDate) <= cancelWindowEnd
        : false

      if (billingVersion !== 'v3') {
        return false
      }

      if (nextPhasePlan === 'community' && !cancelledDuringSale) {
        return true
      }

      if (cancellationDate && !cancelledDuringSale) {
        return true
      }

      return false
    },
    planDiscounts: {
      plus: {
        percentOff: 0.25,
        monthly: { promoCode: 'NUTWB8T7', couponId: 'fSgJCNO1' },
        yearly: { couponId: 'VUl8LFWG' },
      },
      team: {
        percentOff: 0.3,
        monthly: { promoCode: 'V5W1BK8H', couponId: '3aJchyjN' },
        yearly: { couponId: 'ISAVRXdN' },
      },
      enterprise: {
        percentOff: 0.2,
      },
    },
    Button: ({ workspaceId }: { workspaceId: string }) => (
      <Link
        params={{ workspaceId }}
        to="/workspaces/$workspaceId/billing"
        className="flex items-center gap-2 rounded-full bg-gray-400 px-3 py-2 text-white"
        style={{
          backgroundImage: `url(${cyberSaleButtonBgImg})`,
          backgroundSize: 'cover',
        }}
      >
        <Gift className="h-4 w-4" />
        <span className="text-xs">CYBER SALE</span>
      </Link>
    ),
    Banner: ({ workspaceId }: { workspaceId: string }) => (
      <div
        className="flex w-full justify-between rounded-xl bg-blue-500 p-6"
        style={{
          backgroundImage: `url(${cyberSaleBackgroundImg})`,
          backgroundSize: 'cover',
          backgroundPosition: '50%',
        }}
      >
        <div className="flex flex-col gap-1 text-white">
          <div className="text-2xl font-semibold">Cyber Sale is on! Save up to 30%</div>
          <span className="text-sm font-normal">
            Offer ends December 2nd, 2025.{' '}
            <a
              className="underline"
              rel="noreferrer"
              target="_blank"
              href="https://www.botpress.com/legal/cyber-sale-2025-terms-and-conditions"
            >
              T&Cs apply
            </a>
          </span>
        </div>
        <div className="flex items-end md:items-center">
          <Button
            variant="secondary"
            onClick={() => {
              showPlanSelectionDialog({ workspaceId })
            }}
          >
            View Plans
          </Button>
        </div>
      </div>
    ),
  },
  {
    id: 'cyber-sale-2025-december',
    displayName: 'Cyber Sale',
    startDate: new Date('2025-12-01T00:00:00'),
    endDate: new Date('2025-12-02T23:59:59'),
    isElligible: ({ nextPhasePlan, cancellationDate, billingVersion, cancelWindowStart, cancelWindowEnd }) => {
      const cancelledDuringSale = cancellationDate
        ? cancellationDate >= cancelWindowStart && cancellationDate <= cancelWindowEnd
        : false

      if (billingVersion !== 'v3') {
        return false
      }

      if (nextPhasePlan === 'community' && !cancelledDuringSale) {
        return true
      }

      if (cancellationDate && !cancelledDuringSale) {
        return true
      }

      return false
    },
    planDiscounts: {
      plus: {
        percentOff: 0.25,
        monthly: { promoCode: '0FVXPQE6', couponId: 'qD2ixdlb' },
        yearly: { couponId: 'VUl8LFWG' },
      },
      team: {
        percentOff: 0.3,
        monthly: { promoCode: 'GHN24WLG', couponId: 'BBLgYf2a' },
        yearly: { couponId: 'ISAVRXdN' },
      },
      enterprise: {
        percentOff: 0.2,
      },
    },
    Button: ({ workspaceId }: { workspaceId: string }) => (
      <Link
        params={{ workspaceId }}
        to="/workspaces/$workspaceId/billing"
        className="flex items-center gap-2 rounded-full bg-gray-400 px-3 py-2 text-white"
        style={{
          backgroundImage: `url(${cyberSaleButtonBgImg})`,
          backgroundSize: 'cover',
        }}
      >
        <Gift className="h-4 w-4" />
        <span className="text-xs">CYBER SALE</span>
      </Link>
    ),
    Banner: ({ workspaceId }: { workspaceId: string }) => (
      <div
        className="flex w-full justify-between rounded-xl bg-blue-500 p-6"
        style={{
          backgroundImage: `url(${cyberSaleBackgroundImg})`,
          backgroundSize: 'cover',
          backgroundPosition: '50%',
        }}
      >
        <div className="flex flex-col gap-1 text-white">
          <div className="text-2xl font-semibold">Cyber Sale is on! Save up to 30%</div>
          <span>
            Offer ends December 2nd, 2025.{' '}
            <a
              className="underline"
              rel="noreferrer"
              target="_blank"
              href="https://www.botpress.com/legal/cyber-sale-2025-terms-and-conditions"
            >
              T&Cs apply
            </a>
          </span>
        </div>
        <div className="flex items-end md:items-center">
          <Button
            variant="secondary"
            onClick={() => {
              showPlanSelectionDialog({ workspaceId })
            }}
          >
            View Plans
          </Button>
        </div>
      </div>
    ),
  },
]
